import React, { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const facts = [
  { k: "Issuer", v: "BCV AB" },
  { k: "Amount", v: "SEK 10,000,000" },
  { k: "Coupon", v: "12.00% p.a." },
  { k: "Rating", v: "BBB · Bank-guaranteed" },
  { k: "Tenor", v: "36 months" },
  { k: "Min ticket", v: "SEK 100,000" },
];

export const ProspectusSection = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/prospectus/request", form);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      // Trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = "BCV_AB_Bond_Prospectus.pdf";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setDownloaded(true);
      toast.success("Prospectus download started.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to request prospectus.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="prospectus"
      className="relative py-28 md:py-40 bg-ink-900 border-t border-white/5"
      data-testid="prospectus-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-16">
          <div className="font-mono text-[11px] tracking-[0.35em] uppercase text-spectrum-green">
            ◢ Corporate Bond
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
            BCV AB · Placeholder
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
              ◈ Issuing Prospectus · SEK 10M · 12% · BBB
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.92] tracking-tighter mb-8"
              data-testid="prospectus-title"
            >
              BCV AB <span className="spectrum-text">10 MSEK</span> corporate bond.
            </motion.h2>
            <p className="text-white/60 text-base md:text-lg font-light max-w-xl leading-relaxed mb-10">
              An on-demand bank-guaranteed corporate bond, 12% coupon, 36-month tenor.
              Indicative BBB rating driven by the guarantor. Placeholder prospectus
              provided for qualified enquirers only.
            </p>

            <div className="border border-white/10 bg-ink-950">
              <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-spectrum-cyan" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                    bcv-ab.brief
                  </span>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                  INDICATIVE
                </span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
                {facts.map((f, i) => (
                  <div
                    key={f.k}
                    className={`px-5 py-4 ${i >= 2 ? "sm:border-t sm:border-white/10" : ""}`}
                  >
                    <dt className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
                      {f.k}
                    </dt>
                    <dd className="font-mono text-sm md:text-base text-white">{f.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative border border-white/10 bg-ink-950 p-8 md:p-10 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-spectrum-cyan/25 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-spectrum-green mb-3">
                  ◢ Download prospectus
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mb-6">
                  Name & email required
                </h3>
                <p className="text-white/50 text-sm leading-relaxed mb-8">
                  Placeholder document — for demonstration purposes only. We record
                  your details so Erik can follow up with the definitive prospectus.
                </p>

                {downloaded ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="prospectus-success"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-spectrum-green mb-3">
                      ◈ Download started
                    </div>
                    <p className="text-white text-base font-light mb-6">
                      Thank you {form.name.split(" ")[0]}. The placeholder prospectus is
                      downloading. Erik will follow up from{" "}
                      <span className="text-spectrum-green">erik@svenskainvest.com</span>.
                    </p>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/prospectus/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/70 hover:text-white"
                      data-testid="prospectus-redownload"
                    >
                      ↓ download again
                    </a>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="prospectus-form">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                        Name
                      </div>
                      <Input
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Full name"
                        className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                        data-testid="prospectus-name"
                      />
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                        Email
                      </div>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="you@fund.com"
                        className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                        data-testid="prospectus-email"
                      />
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
                        Company (optional)
                      </div>
                      <Input
                        value={form.company}
                        onChange={onChange("company")}
                        placeholder="Institution / AB"
                        className="terminal-input bg-ink-950 border border-white/15 rounded-none font-mono text-sm text-white h-11"
                        data-testid="prospectus-company"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-12 rounded-none bg-spectrum-cyan text-ink-950 hover:bg-white font-mono text-[11px] uppercase tracking-[0.3em] disabled:opacity-60"
                      data-testid="prospectus-submit"
                    >
                      {loading ? "Preparing..." : "↓ Download prospectus (PDF)"}
                    </Button>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 pt-1 leading-relaxed">
                      Not a public offer. Placeholder document. Data processed under
                      GDPR — see privacy notice.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
